import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import Button from 'components/shared/Button';
import { Tip } from 'components/shared/Tip';
import FormTitle from 'components/shared/Typography/FormTitle';
import { AGENT_PERMISSION_CUSTOM_DOMAIN, useIsSubscriptionAllowed } from 'hooks/useIsSubscriptionAllowed';
import { isAgentSelector } from 'store/user/selectors';

import { ACCOUNT_TYPE_AGENT } from '../../../../../../../constants';
import s from './index.module.css';
import ProposalLink from './ProposalLink';
import { UseProposalLinkProps, useProposalLink } from './useProposalLink';
import { useUpgradeButton } from './useUpgradeButton';

type WithClientProps = {
  builder: boolean;
  closeModal: () => void;
  link: string;
} & UseProposalLinkProps;

export function CopyLink(props: WithClientProps) {
  const { itinerariesItemType, link, itemId, builder, closeModal } = props;

  const isAgent = useSelector(isAgentSelector);
  const isCustomDomainPermission = useIsSubscriptionAllowed(ACCOUNT_TYPE_AGENT, AGENT_PERMISSION_CUSTOM_DOMAIN);

  const proposalLinkProps = useProposalLink({ itinerariesItemType, itemId });

  const { data, isFetching } = useQuery(['tail', itemId], proposalLinkProps.loadTailInfo, {
    enabled: isCustomDomainPermission,
    staleTime: 300000,
  });
  const { domain } = (!isFetching && isCustomDomainPermission && data) || {};

  const { onClick: upgrade } = useUpgradeButton({ onClick: closeModal });

  return (
    <Form onSubmit={() => {}}>
      {({ handleSubmit: onSubmit }) => (
        <form onSubmit={onSubmit} className={s.form}>
          <div className={cx(s.fields, { [s.noTabs]: !builder })}>
            <FormTitle hint="Copy and share link with anyone" noBorder>
              Copy Link
            </FormTitle>
            <ProposalLink id={itemId} link={link} {...proposalLinkProps} />
            {isAgent && (
              <Tip type="info">
                {isCustomDomainPermission ? (
                  <span>
                    {isFetching
                      ? ''
                      : domain
                        ? 'You can customize your proposal link by clicking into the end of the URL and typing.'
                        : 'You will be able to customize your link once the Custom Domain will be connected.'}
                  </span>
                ) : (
                  <span>
                    To be able to customize your proposal link, please{' '}
                    <button className={s.upgrade} type="button" onClick={upgrade}>
                      upgrade your plan
                    </button>
                    .
                  </span>
                )}
              </Tip>
            )}
          </div>
          <div className={s.footer}>
            <Button htmlType="submit" disabled className={s.submit}>
              Share
            </Button>
          </div>
        </form>
      )}
    </Form>
  );
}
